import { useApolloClient } from '@apollo/client'
import { useState } from 'react'

import { UpsellRequestResponseDialog } from '@factorialco/f0-react'
import { AvailableModules } from 'generated/featureDefinitions'
import { GetUpsellRequestsByModuleDocument } from 'generated/resources/graphql'

import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'
import { useCreateUpsellRequestWithErrorHandling } from 'modules/upselling_kit/hooks/useCreateUpsellRequest'
import { readLatestUpsellOrigin } from 'modules/upselling_kit/utils/localStorage'

interface UseMarketplaceUpsellRequestProps {
  moduleSlug?: string
  moduleRequested?: AvailableModules
  requestSource: string
  pageSource: string
  requestSourceCategory?: string
  requestSourceComponent?: string
  hubspotModuleName?: string
}

export const useMarketplaceUpsellRequest = ({
  moduleSlug,
  moduleRequested,
  requestSource,
  pageSource,
  requestSourceCategory,
  requestSourceComponent,
  hubspotModuleName,
}: UseMarketplaceUpsellRequestProps) => {
  const [upsellDialogOpen, setUpsellDialogOpen] = useState(false)
  const [upsellSuccess, setUpsellSuccess] = useState(false)
  const { create } = useCreateUpsellRequestWithErrorHandling()
  const apolloClient = useApolloClient()

  const handleUpsellSuccess = () => {
    setUpsellDialogOpen(true)
    setUpsellSuccess(true)
  }

  const handleUpsellError = () => {
    setUpsellDialogOpen(true)
    setUpsellSuccess(false)
  }

  const handleUpsellClose = () => {
    setUpsellDialogOpen(false)
    setUpsellSuccess(false)
  }

  const executeUpsellRequest = async () => {
    const latestUpsellOrigin = readLatestUpsellOrigin()
    const currentRequestSource =
      latestUpsellOrigin?.request_source || requestSource || 'Marketplace'
    const currentCategory =
      latestUpsellOrigin?.request_source_category || requestSourceCategory || 'Organic'
    const currentComponent = latestUpsellOrigin?.request_source_component || requestSourceComponent

    track(trackingEvents.upsell.requestUpsell, {
      source: currentRequestSource,
      component: 'upselling_button',
      module: moduleSlug,
    })

    try {
      await create({
        moduleRequested: moduleRequested,
        requestSource: currentRequestSource,
        requestSourceCategory: currentCategory,
        requestSourceComponent: currentComponent,
        requestReason: ValidReasons.MissingAddon,
        pageSource,
        upsellRequestStatus: 'Opportunity detected',
        upsellRequestType: 'Request a meeting',
        hubspotModuleName,
      })
      handleUpsellSuccess()
      apolloClient.refetchQueries({
        include: [GetUpsellRequestsByModuleDocument],
      })
      return true
    } catch (error) {
      handleUpsellError()
      return false
    }
  }

  const upsellDialog = (
    <UpsellRequestResponseDialog
      open={upsellDialogOpen}
      success={upsellSuccess}
      errorMessage={{
        title: i18n.t('upselling_kit.upselling_button.error_message.title'),
        description: i18n.t('upselling_kit.upselling_button.error_message.description'),
      }}
      successMessage={{
        title: i18n.t('upselling_kit.upselling_button.success_message.title_2'),
        description: i18n.t('upselling_kit.upselling_button.success_message.description'),
      }}
      closeLabel={i18n.t('upselling_kit.upselling_button.close_label')}
      onClose={handleUpsellClose}
    />
  )

  return {
    executeUpsellRequest,
    upsellDialog,
    isDialogOpen: upsellDialogOpen,
    isSuccess: upsellSuccess,
  }
}
